import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { clientsApi, mattersApi, workflowApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    ArrowLeft,
    Buildings,
    FileText,
    Plus,
    TrashSimple,
    Scroll,
    User as UserIcon,
    Phone,
    Envelope,
    MapPin,
    CheckSquare,
    Briefcase,
    Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ClientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(false);

    const load = async () => {
        try {
            setData(await clientsApi.get(id));
        } catch {
            toast.error("Failed to load client");
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const remove = async () => {
        try {
            const r = await clientsApi.remove(id);
            toast.success(r.archived ? "Client archived (had linked matters)" : "Client deleted");
            navigate("/clients");
        } catch {
            toast.error("Delete failed");
        }
    };

    const drawEngagement = async () => {
        try {
            const r = await workflowApi.engagementLetterInstructions(id);
            // Take user to drafting with prefill
            const params = new URLSearchParams({ new: "1" });
            sessionStorage.setItem("draft_prefill", JSON.stringify({
                draft_type: "custom",
                instructions: r.instructions,
                custom_title: `Engagement Letter — ${r.client_name}`,
            }));
            navigate(`/drafting?${params.toString()}`);
        } catch {
            toast.error("Failed to prepare engagement letter");
        }
    };

    if (!data) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
    const c = data.client;

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="client-detail">
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <Link to="/clients" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    <ArrowLeft size={14} /> Clients
                </Link>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={drawEngagement} data-testid="draft-engagement-btn">
                        <Scroll size={14} /> Draft engagement letter
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} data-testid="edit-client-btn">
                        {editing ? "Done editing" : "Edit"}
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="delete-client-btn">
                                <TrashSimple size={14} /> Delete
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete this client?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    If the client has linked matters, the record will be archived instead of permanently deleted. All matters remain.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm">
                                    Delete
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            {/* Header card */}
            <div className="border border-border bg-white rounded-md p-5 mb-6">
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="h-12 w-12 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center shrink-0">
                        {c.type === "corporate" ? <Buildings size={22} weight="duotone" className="text-amber-700" /> : <UserIcon size={22} weight="duotone" className="text-amber-700" />}
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{c.client_id_human}</div>
                        {editing ? (
                            <ClientHeaderEdit client={c} onSaved={load} />
                        ) : (
                            <>
                                <h1 className="font-serif text-3xl tracking-tight" data-testid="client-name">{c.name}</h1>
                                <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground flex-wrap">
                                    {c.phone && <span className="inline-flex items-center gap-1"><Phone size={13} /> {c.phone}</span>}
                                    {c.email && <span className="inline-flex items-center gap-1"><Envelope size={13} /> {c.email}</span>}
                                    {c.address && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {c.address}</span>}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="flex gap-4 text-center text-sm">
                        <Stat icon={Briefcase} label="Matters" value={data.matters.length} />
                        <Stat icon={FileText} label="Documents" value={data.documents.length} />
                        <Stat icon={Scroll} label="Drafts" value={data.drafts.length} />
                        <Stat icon={CheckSquare} label="Open tasks" value={data.open_tasks.length} />
                    </div>
                </div>
                {c.notes && <p className="text-sm text-muted-foreground mt-4 border-t border-border pt-3 italic">{c.notes}</p>}
            </div>

            <Tabs defaultValue="matters">
                <TabsList>
                    <TabsTrigger value="matters" data-testid="tab-matters">Matters ({data.matters.length})</TabsTrigger>
                    <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({c.contacts?.length || 0})</TabsTrigger>
                    <TabsTrigger value="docs" data-testid="tab-docs">Documents ({data.documents.length})</TabsTrigger>
                    <TabsTrigger value="kyc" data-testid="tab-kyc">KYC ({data.kyc_documents.length})</TabsTrigger>
                    <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks ({data.open_tasks.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="matters">
                    <MattersList matters={data.matters} clientId={c.id} clientName={c.name} onAdded={load} />
                </TabsContent>

                <TabsContent value="contacts">
                    <Contacts client={c} onChanged={load} />
                </TabsContent>

                <TabsContent value="docs">
                    {!data.documents.length ? <Empty icon={FileText} label="No documents linked to this client's matters." /> : (
                        <ul className="border border-border bg-white rounded-md divide-y divide-border">
                            {data.documents.map((d) => (
                                <li key={d.id} className="p-3 text-sm flex items-center gap-2">
                                    <FileText size={14} className="text-muted-foreground" />
                                    <Link to={`/documents/${d.id}`} className="hover:underline">{d.original_filename}</Link>
                                    <span className="ml-auto text-xs text-muted-foreground">{new Date(d.created_at).toLocaleString()}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </TabsContent>

                <TabsContent value="kyc">
                    {!data.kyc_documents.length ? (
                        <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground">
                            No KYC documents linked. Upload Aadhaar / PAN / Passport in <Link to="/documents" className="underline">Documents</Link>, then use the document's matter dropdown to associate it here (or via the KYC tab API).
                        </div>
                    ) : (
                        <ul className="border border-border bg-white rounded-md divide-y divide-border">
                            {data.kyc_documents.map((d) => (
                                <li key={d.id} className="p-3 text-sm flex items-center gap-2">
                                    <FileText size={14} className="text-muted-foreground" />
                                    <Link to={`/documents/${d.id}`} className="hover:underline">{d.original_filename}</Link>
                                    {d.kyc_type && <span className="ml-2 chip chip-doc">{d.kyc_type}</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                </TabsContent>

                <TabsContent value="tasks">
                    {!data.open_tasks.length ? <Empty icon={CheckSquare} label="No open tasks for this client." /> : (
                        <ul className="border border-border bg-white rounded-md divide-y divide-border">
                            {data.open_tasks.map((t) => (
                                <li key={t.id} className="p-3 text-sm flex items-center gap-3">
                                    <span className={`chip ${t.priority === "urgent" ? "chip-doc text-destructive" : "chip-ai"}`}>{t.priority}</span>
                                    <span>{t.title}</span>
                                    {t.due_date && <span className="ml-auto text-xs text-muted-foreground">due {t.due_date}</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div className="px-3 py-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center justify-center gap-1">
                <Icon size={11} /> {label}
            </div>
            <div className="text-2xl font-serif">{value}</div>
        </div>
    );
}

function Empty({ icon: Icon, label }) {
    return (
        <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">
            <Icon size={20} className="mx-auto mb-2 text-muted-foreground" weight="duotone" />
            {label}
        </div>
    );
}

function ClientHeaderEdit({ client, onSaved }) {
    const [v, setV] = useState({ name: client.name, phone: client.phone || "", email: client.email || "", address: client.address || "", notes: client.notes || "" });
    const save = async () => {
        try {
            await clientsApi.update(client.id, v);
            toast.success("Saved");
            onSaved?.();
        } catch {
            toast.error("Save failed");
        }
    };
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
            <Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder="Name" />
            <Input value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} placeholder="Phone" />
            <Input value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} placeholder="Email" />
            <Input value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} placeholder="Address" />
            <Input className="md:col-span-2" value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} placeholder="Notes" />
            <Button size="sm" onClick={save} className="md:col-span-2 w-fit"><Sparkle size={13} /> Save</Button>
        </div>
    );
}

function MattersList({ matters, clientId, clientName, onAdded }) {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <div className="text-sm text-muted-foreground">{matters.length} matter(s) linked to this client</div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="new-matter-for-client"><Plus size={13} /> New matter</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>New matter for {clientName}</DialogTitle>
                            <DialogDescription>The client will be pre-linked.</DialogDescription>
                        </DialogHeader>
                        <NewMatterForm clientId={clientId} clientName={clientName} onDone={() => { setOpen(false); onAdded(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {!matters.length ? <Empty icon={Briefcase} label="No matters yet — click 'New matter' to link this client to their first case." /> : (
                <ul className="border border-border bg-white rounded-md divide-y divide-border">
                    {matters.map((m) => (
                        <li key={m.id} className="p-3 text-sm flex items-center gap-3">
                            <Briefcase size={14} className="text-muted-foreground" />
                            <Link to={`/matters/${m.id}`} className="hover:underline font-medium">{m.title}</Link>
                            {m.case_number && <span className="chip chip-doc">{m.case_number}</span>}
                            {m.court && <span className="text-xs text-muted-foreground">{m.court}</span>}
                            {m.status_stage && <span className="ml-auto chip chip-ai">{m.status_stage}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function NewMatterForm({ clientId, clientName, onDone }) {
    const [v, setV] = useState({ title: "", court: "", case_number: "", opposing_party: "" });
    const submit = async () => {
        if (!v.title.trim()) { toast.error("Title required"); return; }
        try {
            await mattersApi.create({
                ...v, client_id: clientId, client_name: clientName,
            });
            toast.success("Matter created");
            onDone?.();
        } catch {
            toast.error("Failed to create matter");
        }
    };
    return (
        <div className="space-y-2">
            <Input placeholder="Title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="new-matter-title" />
            <Input placeholder="Court" value={v.court} onChange={(e) => setV({ ...v, court: e.target.value })} />
            <Input placeholder="Case number" value={v.case_number} onChange={(e) => setV({ ...v, case_number: e.target.value })} />
            <Input placeholder="Opposing party" value={v.opposing_party} onChange={(e) => setV({ ...v, opposing_party: e.target.value })} />
            <div className="flex justify-end pt-1">
                <Button size="sm" onClick={submit} data-testid="new-matter-submit">Create</Button>
            </div>
        </div>
    );
}

function Contacts({ client, onChanged }) {
    const [open, setOpen] = useState(false);
    const remove = async (cid) => {
        try {
            await clientsApi.removeContact(client.id, cid);
            onChanged?.();
        } catch {
            toast.error("Failed to remove");
        }
    };
    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <div className="text-sm text-muted-foreground">{client.contacts?.length || 0} contact(s)</div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="add-contact-btn"><Plus size={13} /> Add contact</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Add contact</DialogTitle></DialogHeader>
                        <AddContactForm clientId={client.id} onDone={() => { setOpen(false); onChanged(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {!client.contacts?.length ? <Empty icon={UserIcon} label="No additional contacts yet." /> : (
                <ul className="border border-border bg-white rounded-md divide-y divide-border">
                    {client.contacts.map((cc) => (
                        <li key={cc.id} className="p-3 text-sm flex items-center gap-3" data-testid={`contact-${cc.id}`}>
                            <UserIcon size={14} className="text-muted-foreground" />
                            <div className="flex-1">
                                <div className="font-medium">{cc.name}</div>
                                <div className="text-xs text-muted-foreground">{cc.role}{cc.designation ? ` · ${cc.designation}` : ""}{cc.is_poa_holder ? " · POA holder" : ""}</div>
                                {(cc.phone || cc.email) && <div className="text-xs text-muted-foreground">{cc.phone} {cc.email}</div>}
                            </div>
                            <button className="text-muted-foreground hover:text-destructive" onClick={() => remove(cc.id)} aria-label="Remove">
                                <TrashSimple size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function AddContactForm({ clientId, onDone }) {
    const [v, setV] = useState({ name: "", role: "POC", phone: "", email: "", designation: "", is_poa_holder: false });
    const submit = async () => {
        if (!v.name.trim()) return;
        try {
            await clientsApi.addContact(clientId, v);
            toast.success("Contact added");
            onDone?.();
        } catch {
            toast.error("Failed to add");
        }
    };
    return (
        <div className="space-y-2">
            <Input placeholder="Name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="contact-name" />
            <Input placeholder="Role (e.g., Authorized Signatory, POC, POA Holder)" value={v.role} onChange={(e) => setV({ ...v, role: e.target.value })} />
            <Input placeholder="Designation" value={v.designation} onChange={(e) => setV({ ...v, designation: e.target.value })} />
            <Input placeholder="Phone" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} />
            <Input placeholder="Email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} />
            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={v.is_poa_holder} onChange={(e) => setV({ ...v, is_poa_holder: e.target.checked })} className="accent-amber-600" />
                Power of Attorney holder
            </label>
            <div className="flex justify-end">
                <Button size="sm" onClick={submit} data-testid="contact-save">Save</Button>
            </div>
        </div>
    );
}
